/**
 * LessonEdit 컴포넌트 - 레슨 생성/편집 페이지
 *
 * 주요 흐름:
 * 1. URL에서 lessonId 또는 courseId/chapterId 추출
 * 2. 편집 모드인 경우 기존 레슨 정보 fetch
 * 3. 폼 입력값 관리 (useState)
 * 4. 저장 버튼 클릭 시 API 호출
 * 5. 저장 후 강의 상세 페이지로 이동
 */

import { useQueryClient } from '@tanstack/react-query';
import { LESSON_KIND } from '@theailab-dev/parchment';
import type { LessonKind } from '@theailab-dev/parchment';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createLesson, updateLesson } from '../../api/lessonApi';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useRemoveWithConfirm } from '../../hooks';
import { useCourseDetail } from '../../hooks/useCourseDetail';
import { useLessonDetail } from '../../hooks/useLessonDetail';
import { formatDateTimeLocal } from '../../utils/dateTime';
import { getErrorMessage } from '../../utils/errorHandler';
import { getMaterialDisplayName } from '../../utils/materialUrl';

const LessonEdit: React.FC = () => {
  const { id, courseId, chapterId } = useParams<{
    id?: string;
    courseId?: string;
    chapterId?: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const lessonId = id ? parseInt(id, 10) : null;
  const parsedCourseId = courseId ? parseInt(courseId, 10) : null;
  const parsedChapterId = chapterId ? parseInt(chapterId, 10) : null;
  const isEditMode = lessonId !== null;

  /**
   * 편집 모드인 경우 기존 레슨 정보 fetch
   */
  const { data: lesson, isLoading } = useLessonDetail(lessonId || 0, {
    enabled: isEditMode,
  });

  /**
   * 생성 모드인 경우 강의 상세 정보 fetch (레슨 seq 계산용)
   */
  const { data: course } = useCourseDetail(parsedCourseId || 0, {
    enabled: !isEditMode && !!parsedCourseId,
  });

  /**
   * 폼 상태 관리
   */
  const [formData, setFormData] = useState({
    title: '',
    kind: '' as LessonKind,
    isPreviewable: false,
    startAt: '',
    endAt: '',
    content: '',
    joinUrl: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHtmlSource, setShowHtmlSource] = useState(false);

  // 동영상 파일 업로드 상태 (VIDEO, RETAKE 타입일 때 사용)
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [_showVideoFileInput, setShowVideoFileInput] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  // 레슨 자료 (편집/생성 시 파일 선택, 제거 시 materialsUrl: null)
  const [materialFile, setMaterialFile] = useState<File | null>(null);
  const materialFileInputRef = useRef<HTMLInputElement>(null);

  /** 레슨 자료 제거: 확인 모달 → 즉시 PATCH → 캐시 무효화 */
  const removeMaterial = useRemoveWithConfirm({
    mutationFn: (lid: number) => updateLesson(lid, { materialsUrl: null }),
    invalidateKeys: (lid) => [['lesson', lid], ...(lesson?.course?.id ? [['course', lesson.course.id]] : [])],
    title: '레슨 자료 제거',
    message: '레슨 자료 링크를 제거하시겠습니까?',
    onError: (err) => setError(getErrorMessage(err)),
  });

  // 업로드 중 이탈 방지
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isSubmitting) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    if (isSubmitting) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isSubmitting]);

  // Tiptap 에디터
  const contentEditor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Image,
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData((prev) => ({ ...prev, content: editor.getHTML() }));
    },
  });

  // 편집 모드인 경우 에디터 내용 업데이트
  useEffect(() => {
    if (contentEditor && lesson && isEditMode) {
      contentEditor.commands.setContent(lesson.content || '');
    }
  }, [contentEditor, lesson, isEditMode]);

  /**
   * 편집 모드인 경우 기존 데이터로 폼 초기화
   */
  useEffect(() => {
    if (lesson && isEditMode) {
      setFormData({
        title: lesson.title || '',
        kind: lesson.kind,
        isPreviewable: lesson.isPreviewable || false,
        startAt: formatDateTimeLocal(lesson.startAt),
        endAt: formatDateTimeLocal(lesson.endAt),
        content: lesson.content || '',
        joinUrl: lesson.joinUrl || '',
      });
    }
  }, [lesson, isEditMode]);

  /**
   * 입력 필드 변경 핸들러
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  /**
   * 동영상 파일 선택 핸들러
   */
  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoFile(file);
      // 미리보기 생성
      const videoUrl = URL.createObjectURL(file);
      setVideoPreview(videoUrl);
    }
  };

  /**
   * 동영상 파일 제거 핸들러
   */
  const handleRemoveVideoFile = () => {
    setVideoFile(null);
    setVideoPreview(null);
    setShowVideoFileInput(false);
    // 파일 입력 초기화
    const fileInput = document.getElementById('videoFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  /**
   * 동영상 변경 버튼 클릭 핸들러
   * 파일 선택 다이얼로그를 바로 열기
   */
  const handleChangeVideo = () => {
    const fileInput = document.getElementById('videoFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  /**
   * 동영상 변경 취소 핸들러
   */
  const handleCancelVideoChange = () => {
    setShowVideoFileInput(false);
    setVideoFile(null);
    setVideoPreview(null);
    const fileInput = document.getElementById('videoFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  /**
   * 저장 버튼 핸들러
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // 필수 필드 검증
    if (!formData.title.trim()) {
      setError('레슨 제목을 입력해주세요.');
      return;
    }
    if (!formData.kind) {
      setError('레슨 종류를 선택해주세요.');
      return;
    }

    // VIDEO 타입일 때 video 파일 필수 검증 (생성 모드만)
    if (!isEditMode && (formData.kind === LESSON_KIND.VIDEO || formData.kind === LESSON_KIND.RETAKE) && !videoFile) {
      setError('동영상 강의는 동영상 파일이 필요합니다.');
      return;
    }

    // ZOOM 타입일 때 joinUrl 필수 검증
    if (formData.kind === LESSON_KIND.ZOOM && !formData.joinUrl?.trim()) {
      setError('ZOOM 참여 URL을 입력해주세요.');
      return;
    }

    // ZOOM 타입일 때 시작/종료 시각 검증
    if (formData.kind === LESSON_KIND.ZOOM) {
      if (!formData.startAt || !formData.endAt) {
        setError('ZOOM 강의의 시작 시각과 종료 시각을 모두 입력해주세요.');
        return;
      }
      if (new Date(formData.endAt) <= new Date(formData.startAt)) {
        setError('강의 종료 시각은 시작 시각보다 이후여야 합니다.');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && lessonId && lesson) {
        // 편집 모드: 레슨 수정 - 변경된 필드만 전송
        const updateData: Record<string, any> = {};

        // 제목 비교
        const trimmedTitle = formData.title.trim();
        const lessonTitle = lesson.title || '';
        if (trimmedTitle !== lessonTitle) {
          updateData.title = trimmedTitle;
        }

        // 종류 비교 (값이 없어도 원래 값과 다르면 변경사항으로 처리)
        const formKind = formData.kind;
        if (formKind !== lesson.kind) {
          updateData.kind = formKind;
        }

        // 미리보기 가능 여부 비교
        if (formData.isPreviewable !== lesson.isPreviewable) {
          updateData.isPreviewable = formData.isPreviewable;
        }

        // 시작 시간 비교 (ISO 문자열로 정규화하여 비교)
        const formStartAtISO = formData.startAt ? new Date(formData.startAt).toISOString() : null;
        const lessonStartAtISO = lesson.startAt ? new Date(lesson.startAt).toISOString() : null;
        if (formStartAtISO !== lessonStartAtISO) {
          updateData.startAt = formData.startAt ? new Date(formData.startAt) : null;
        }

        // 종료 시간 비교
        const formEndAtISO = formData.endAt ? new Date(formData.endAt).toISOString() : null;
        const lessonEndAtISO = lesson.endAt ? new Date(lesson.endAt).toISOString() : null;
        if (formEndAtISO !== lessonEndAtISO) {
          updateData.endAt = formData.endAt ? new Date(formData.endAt) : null;
        }

        // 콘텐츠 비교 (null, undefined, 빈 문자열 정규화)
        const trimmedContent = formData.content?.trim() || '';
        const lessonContent = lesson.content || '';
        if (trimmedContent !== lessonContent) {
          updateData.content = trimmedContent || null;
        }

        // ZOOM URL 비교 (null, undefined, 빈 문자열 정규화)
        const trimmedJoinUrl = formData.joinUrl?.trim() || '';
        const lessonJoinUrl = lesson.joinUrl || '';
        if (trimmedJoinUrl !== lessonJoinUrl) {
          updateData.joinUrl = trimmedJoinUrl || null;
        }

        // 동영상 파일 변경 확인
        const hasNewVideoFile =
          (formData.kind === LESSON_KIND.VIDEO || formData.kind === LESSON_KIND.RETAKE) && videoFile !== null;
        const hasNewMaterialFile = materialFile !== null;

        // 변경사항이 있거나 새 동영상/레슨 자료 파일이 있는 경우에만 API 호출
        const hasChanges = Object.keys(updateData).length > 0 || hasNewVideoFile || hasNewMaterialFile;

        if (hasChanges) {
          await updateLesson(
            lessonId,
            updateData,
            hasNewVideoFile ? (videoFile ?? undefined) : undefined,
            hasNewMaterialFile ? (materialFile ?? undefined) : undefined,
            (progressEvent) => {
              const total = progressEvent.total || progressEvent.estimated || 0;
              if (total > 0) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
                console.log(`Upload progress: ${percentCompleted}%`);
                setUploadProgress(percentCompleted);
              }
            },
          );

          // 캐시 무효화
          await queryClient.invalidateQueries({ queryKey: ['lesson', lessonId] });
          if (lesson) {
            await queryClient.invalidateQueries({
              queryKey: ['course', lesson.course.id],
            });
          }
        }

        // 변경사항이 없어도 페이지 이동
        if (lesson) {
          navigate(`/dashboard/courses/${lesson.course.id}`);
        }
      } else if (parsedCourseId && parsedChapterId) {
        // 생성 모드: 레슨 생성
        // 챕터의 기존 레슨 개수를 확인하여 seq 계산
        const chapter = course?.chapters?.find((ch) => ch.id === parsedChapterId);
        const existingLessonsCount = chapter?.lessons?.length || 0;
        const nextSeq = existingLessonsCount + 1;

        const requestData = {
          title: formData.title.trim(),
          kind: formData.kind,
          seq: nextSeq,
          isPreviewable: formData.isPreviewable,
          ...(formData.startAt && { startAt: new Date(formData.startAt) }),
          ...(formData.endAt && { endAt: new Date(formData.endAt) }),
          ...(formData.content?.trim() && { content: formData.content.trim() }),
          ...(formData.joinUrl?.trim() && { joinUrl: formData.joinUrl.trim() }),
        };

        // 레슨 생성 시 동영상/레슨 자료 파일 함께 전송
        await createLesson(
          parsedChapterId,
          requestData,
          formData.kind === LESSON_KIND.VIDEO || formData.kind === LESSON_KIND.RETAKE
            ? videoFile || undefined
            : undefined,
          materialFile ?? undefined,
          (progressEvent) => {
            const total = progressEvent.total || progressEvent.estimated || 0;
            if (total > 0) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
              console.log(`Upload progress: ${percentCompleted}%`);
              setUploadProgress(percentCompleted);
            }
          },
        );

        // 페이지 이동 후 CourseDetail에서 자동으로 최신 데이터를 로드하므로 invalidateQueries 불필요
        // 하지만 확실한 갱신을 위해 명시적으로 무효화
        await queryClient.invalidateQueries({
          queryKey: ['course', parsedCourseId],
        });

        // 강의 상세로 이동
        navigate(`/dashboard/courses/${parsedCourseId}`);
      }
    } catch (error: unknown) {
      console.error('❌ 레슨 저장 실패:', error);
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (error as { message?: string })?.message ||
        '레슨 저장에 실패했습니다. 다시 시도해주세요.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 취소 버튼 핸들러
   */
  const handleCancel = () => {
    if (isEditMode && lesson) {
      navigate(`/dashboard/lessons/${lesson.id}`);
    } else if (parsedCourseId) {
      navigate(`/dashboard/courses/${parsedCourseId}`);
    } else {
      navigate(-1);
    }
  };

  // 로딩 상태
  if (isEditMode && isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const isVideoOrRetake = formData.kind === LESSON_KIND.VIDEO || formData.kind === LESSON_KIND.RETAKE;

  return (
    <>
      {/* 전체 화면 로딩 오버레이 */}
      {isSubmitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-8 shadow-xl">
            <div className="text-center">
              <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent"></div>
              <h3 className="mb-2 text-lg font-semibold text-slate-900">
                {formData.kind === LESSON_KIND.VIDEO || (formData.kind === LESSON_KIND.RETAKE && videoFile)
                  ? uploadProgress === 100
                    ? '서버에서 처리 중...'
                    : '동영상 업로드 중...'
                  : '처리 중...'}
              </h3>
              <p className="text-sm text-slate-600">
                {formData.kind === LESSON_KIND.VIDEO || (formData.kind === LESSON_KIND.RETAKE && videoFile)
                  ? uploadProgress === 100
                    ? '업로드가 완료되었습니다. 서버에서 동영상을 처리하고 있습니다.'
                    : '동영상 파일을 업로드하고 있습니다. 잠시만 기다려주세요.'
                  : '레슨 정보를 저장하고 있습니다.'}
              </p>
              {uploadProgress !== null && (
                <div className="mt-4">
                  <div className="h-2.5 w-full rounded-full bg-slate-200">
                    <div
                      className="h-2.5 rounded-full bg-indigo-600 transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                  <p className="mt-1 text-right text-xs text-slate-500">{uploadProgress}%</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="flex-1 overflow-auto bg-slate-100 p-8">
        <div className="mx-auto max-w-4xl">
          {/* 헤더 */}
          <div className="mb-8">
            <h2 className="m-0 text-3xl font-bold text-slate-800">{isEditMode ? '레슨 편집' : '새 레슨 만들기'}</h2>
            <p className="mt-2 text-slate-600">
              {isEditMode ? '레슨의 정보를 수정합니다.' : '새로운 레슨을 생성합니다.'}
            </p>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* 폼 */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 기본 정보 */}
            <div className="rounded-xl bg-white p-6 shadow-sm">
              <h3 className="mb-6 text-xl font-semibold text-slate-800">기본 정보</h3>

              <div className="space-y-4">
                {/* 제목 */}
                <div>
                  <label htmlFor="title" className="mb-2 block text-sm font-medium text-slate-700">
                    레슨 제목 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="레슨 제목을 입력하세요"
                  />
                </div>

                {/* 종류 */}
                <div>
                  <label htmlFor="kind" className="mb-2 block text-sm font-medium text-slate-700">
                    레슨 종류 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="kind"
                    name="kind"
                    value={formData.kind}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none">
                    <option value="">선택하세요</option>
                    <option value={LESSON_KIND.VIDEO}>동영상 강의</option>
                    <option value={LESSON_KIND.ZOOM}>실시간 강의 (ZOOM)</option>
                    <option value={LESSON_KIND.RETAKE}>실시간 강의 녹화본</option>
                  </select>
                </div>

                {/* 미리보기 가능 여부 */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isPreviewable"
                    name="isPreviewable"
                    checked={formData.isPreviewable}
                    onChange={handleChange}
                    className="h-5 w-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="isPreviewable" className="text-sm font-medium text-slate-700">
                    미리보기 가능
                  </label>
                </div>
              </div>
            </div>

            {/* 콘텐츠 정보 */}
            <div className="rounded-xl bg-white p-6 shadow-sm">
              <h3 className="mb-6 text-xl font-semibold text-slate-800">콘텐츠 정보</h3>

              <div className="space-y-4">
                {/* 콘텐츠 */}
                <div>
                  <label htmlFor="content" className="mb-2 block text-sm font-medium text-slate-700">
                    콘텐츠 설명
                  </label>
                  <div className="overflow-hidden rounded-lg border border-slate-300 focus-within:border-transparent focus-within:ring-2 focus-within:ring-indigo-500">
                    {/* 툴바 */}
                    {contentEditor && (
                      <div className="flex flex-wrap gap-1 border-b border-slate-200 bg-slate-50 p-2">
                        <button
                          type="button"
                          onClick={() => contentEditor.chain().focus().toggleBold().run()}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            contentEditor.isActive('bold') ? 'bg-slate-300' : ''
                          }`}>
                          <strong>B</strong>
                        </button>
                        <button
                          type="button"
                          onClick={() => contentEditor.chain().focus().toggleItalic().run()}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            contentEditor.isActive('italic') ? 'bg-slate-300' : ''
                          }`}>
                          <em>I</em>
                        </button>
                        <button
                          type="button"
                          onClick={() => contentEditor.chain().focus().toggleUnderline().run()}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            contentEditor.isActive('underline') ? 'bg-slate-300' : ''
                          }`}>
                          <u>U</u>
                        </button>
                        <button
                          type="button"
                          onClick={() => contentEditor.chain().focus().toggleStrike().run()}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            contentEditor.isActive('strike') ? 'bg-slate-300' : ''
                          }`}>
                          <s>S</s>
                        </button>
                        <div className="mx-1 h-6 w-px bg-slate-300" />
                        <button
                          type="button"
                          onClick={() => contentEditor.chain().focus().toggleHeading({ level: 1 }).run()}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            contentEditor.isActive('heading', { level: 1 }) ? 'bg-slate-300' : ''
                          }`}>
                          H1
                        </button>
                        <button
                          type="button"
                          onClick={() => contentEditor.chain().focus().toggleHeading({ level: 2 }).run()}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            contentEditor.isActive('heading', { level: 2 }) ? 'bg-slate-300' : ''
                          }`}>
                          H2
                        </button>
                        <button
                          type="button"
                          onClick={() => contentEditor.chain().focus().toggleHeading({ level: 3 }).run()}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            contentEditor.isActive('heading', { level: 3 }) ? 'bg-slate-300' : ''
                          }`}>
                          H3
                        </button>
                        <div className="mx-1 h-6 w-px bg-slate-300" />
                        <button
                          type="button"
                          onClick={() => contentEditor.chain().focus().toggleBulletList().run()}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            contentEditor.isActive('bulletList') ? 'bg-slate-300' : ''
                          }`}>
                          •
                        </button>
                        <button
                          type="button"
                          onClick={() => contentEditor.chain().focus().toggleOrderedList().run()}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            contentEditor.isActive('orderedList') ? 'bg-slate-300' : ''
                          }`}>
                          1.
                        </button>
                        <div className="mx-1 h-6 w-px bg-slate-300" />
                        <button
                          type="button"
                          onClick={() => {
                            const url = window.prompt('링크 URL을 입력하세요:');
                            if (url) {
                              contentEditor.chain().focus().setLink({ href: url }).run();
                            }
                          }}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            contentEditor.isActive('link') ? 'bg-slate-300' : ''
                          }`}>
                          링크
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const url = window.prompt('이미지 URL을 입력하세요:');
                            if (url) {
                              contentEditor.chain().focus().setImage({ src: url }).run();
                            }
                          }}
                          className="rounded px-2 py-1 text-sm hover:bg-slate-200">
                          이미지
                        </button>
                        <div className="mx-1 h-6 w-px bg-slate-300" />
                        <button
                          type="button"
                          onClick={() => setShowHtmlSource(!showHtmlSource)}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            showHtmlSource ? 'bg-slate-300' : ''
                          }`}>
                          HTML
                        </button>
                      </div>
                    )}
                    {/* 에디터 또는 HTML 소스 */}
                    {showHtmlSource ? (
                      <textarea
                        value={contentEditor?.getHTML() || ''}
                        onChange={(e) => {
                          if (contentEditor) {
                            contentEditor.commands.setContent(e.target.value);
                          }
                        }}
                        className="min-h-[200px] w-full resize-y border-none p-4 font-mono text-sm outline-none"
                        placeholder="HTML 코드를 입력하세요..."
                      />
                    ) : (
                      <EditorContent editor={contentEditor} className="prose prose-sm min-h-[200px] max-w-none p-4" />
                    )}
                  </div>
                </div>

                {/* 동영상 파일 업로드 - VIDEO, RETAKE 타입일 때 */}
                {isVideoOrRetake && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">동영상 파일</label>
                    {/* 새로 선택한 파일이 있을 때 */}
                    {videoFile ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-3">
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-slate-700">{videoFile.name}</span>
                            <span className="text-xs text-slate-500">
                              ({(videoFile.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                            <span className="text-xs font-medium text-indigo-600">(새로 업로드할 파일)</span>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemoveVideoFile}
                            className="rounded bg-red-100 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-200">
                            제거
                          </button>
                        </div>
                        {videoPreview && (
                          <div className="mt-2">
                            <video
                              src={videoPreview}
                              controls
                              className="max-h-64 w-full rounded-lg border border-slate-200"
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* 기존 동영상 정보 (편집 모드일 때만) */}
                        {isEditMode && lesson?.videoUrl && !videoFile ? (
                          <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                            <div className="mb-3 flex items-center justify-between">
                              <span className="text-sm font-medium text-indigo-800">현재 동영상</span>
                              <a
                                href={lesson.videoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline">
                                동영상 보기
                              </a>
                            </div>
                            <video
                              src={lesson.videoUrl}
                              controls
                              className="max-h-64 w-full rounded-lg border border-indigo-200"
                            />
                            <div className="mt-3 flex justify-end">
                              <button
                                type="button"
                                onClick={handleChangeVideo}
                                className="rounded-lg border border-indigo-300 bg-white px-4 py-2 text-sm font-medium text-indigo-700 transition-colors hover:bg-indigo-50">
                                동영상 변경
                              </button>
                            </div>
                            {/* 숨겨진 파일 입력 필드 */}
                            <input
                              type="file"
                              id="videoFile"
                              name="videoFile"
                              accept="video/*"
                              onChange={handleVideoFileChange}
                              className="hidden"
                            />
                          </div>
                        ) : (
                          <>
                            {/* 파일 선택 필드 */}
                            <input
                              type="file"
                              id="videoFile"
                              name="videoFile"
                              accept="video/*"
                              onChange={handleVideoFileChange}
                              className="w-full rounded-lg border border-slate-300 px-4 py-2.5 file:mr-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                            />
                            {/* 변경 취소 버튼 (편집 모드이고 기존 동영상이 있을 때만) */}
                            {isEditMode && lesson?.videoUrl && videoFile && (
                              <button
                                type="button"
                                onClick={handleCancelVideoChange}
                                className="rounded-lg border border-slate-300 bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200">
                                취소
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ZOOM URL - ZOOM 타입일 때만 */}
                {formData.kind === LESSON_KIND.ZOOM && (
                  <div>
                    <label htmlFor="joinUrl" className="mb-2 block text-sm font-medium text-slate-700">
                      ZOOM 참여 URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      id="joinUrl"
                      name="joinUrl"
                      value={formData.joinUrl}
                      onChange={handleChange}
                      required={formData.kind === LESSON_KIND.ZOOM}
                      className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholder="https://zoom.us/j/..."
                    />
                    <p className="mt-1 text-xs text-slate-500">학생들이 실시간 강의에 참여할 ZOOM URL을 입력하세요</p>
                  </div>
                )}
              </div>
            </div>

            {/* 레슨 자료 (편집/생성 모드) */}
            <div className="rounded-xl bg-white p-6 shadow-sm">
              <h3 className="mb-6 text-xl font-semibold text-slate-800">레슨 자료</h3>
              <div className="space-y-4">
                {(lesson as { materialsUrl?: string | null } | undefined)?.materialsUrl ? (
                  <>
                    <div className="flex flex-wrap items-center gap-4">
                      <a
                        href={(lesson as { materialsUrl: string }).materialsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="max-w-md truncate text-indigo-600 hover:underline"
                        title={(lesson as { materialsUrl: string }).materialsUrl}>
                        {getMaterialDisplayName((lesson as { materialsUrl: string }).materialsUrl)}
                      </a>
                      {isEditMode && lessonId && (
                        <button
                          type="button"
                          onClick={removeMaterial.openConfirm}
                          disabled={removeMaterial.isPending}
                          className="rounded-lg bg-slate-200 px-4 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-300 disabled:opacity-50">
                          {removeMaterial.isPending ? '처리 중...' : '레슨 자료 제거'}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      단일 파일 등록 가능. 새 파일을 선택하면 저장 시 기존 자료를 대체합니다.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-slate-600">
                    등록된 레슨 자료가 없습니다. 아래에서 파일을 선택한 뒤 저장하면 등록됩니다.
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <input
                    ref={materialFileInputRef}
                    type="file"
                    name="materialFile"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.hwp,image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setMaterialFile(file ?? null);
                    }}
                    className="hidden"
                  />
                  {materialFile ? (
                    <>
                      <span className="max-w-xs truncate text-sm text-slate-700">{materialFile.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setMaterialFile(null);
                          if (materialFileInputRef.current) materialFileInputRef.current.value = '';
                        }}
                        className="rounded-lg bg-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-300">
                        선택 취소
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => materialFileInputRef.current?.click()}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                      {(lesson as { materialsUrl?: string | null } | undefined)?.materialsUrl
                        ? '파일 선택 (변경)'
                        : '파일 선택 (등록)'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* 기타 정보 */}
            {formData.kind === LESSON_KIND.ZOOM && (
              <div className="rounded-xl bg-white p-6 shadow-sm">
                <h3 className="mb-6 text-xl font-semibold text-slate-800">실시간 강의 정보</h3>

                <div className="space-y-4">
                  {/* 시작 시각 */}
                  <div>
                    <label htmlFor="startAt" className="mb-2 block text-sm font-medium text-slate-700">
                      강의 시작 시각 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      id="startAt"
                      name="startAt"
                      value={formData.startAt}
                      onChange={handleChange}
                      required={formData.kind === LESSON_KIND.ZOOM}
                      className="w-full max-w-md rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-slate-500">ZOOM 실시간 강의가 시작되는 시간을 입력하세요</p>
                  </div>

                  {/* 종료 시각 */}
                  <div>
                    <label htmlFor="endAt" className="mb-2 block text-sm font-medium text-slate-700">
                      강의 종료 시각 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      id="endAt"
                      name="endAt"
                      value={formData.endAt}
                      onChange={handleChange}
                      required={formData.kind === LESSON_KIND.ZOOM}
                      className="w-full max-w-md rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-slate-500">ZOOM 실시간 강의가 종료되는 시간을 입력하세요</p>
                  </div>
                </div>
              </div>
            )}

            {/* 버튼 영역 */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-lg bg-slate-200 px-6 py-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-300">
                취소
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-lg bg-indigo-600 px-6 py-2.5 font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
                {isSubmitting ? '처리 중...' : isEditMode ? '저장' : '생성'}
              </button>
            </div>
          </form>

          {lessonId && <ConfirmModal {...removeMaterial.getConfirmModalProps(lessonId)} />}
        </div>
      </div>
    </>
  );
};

export default LessonEdit;
